import { WasteHandoverPlanModel } from "services/Main/wasteHandoverPlan_services";
import { EditableFormInstance, EditableProTable, ProColumns } from "@ant-design/pro-components";
import { Form, Input, InputNumber, Select } from "antd";
import { useTranslation } from "react-i18next";
import { customTableClassName } from "base/ui/components/CustomTable";
import { uiStore } from "stores/root_store";
import { CardBox } from "./cardBox";

interface HandoverInfoCreateProps {
    editTableForm: any;
    editableFormRef: React.MutableRefObject<EditableFormInstance | undefined>;
    dataSource: WasteHandoverPlanModel[];
    wasteOptions: { label: string; value: string; productName?: string }[];
    storageOptions: { label: string; value: string }[];
    dataForCreate: WasteHandoverPlanModel[];
    dataFromList: WasteHandoverPlanModel[] | undefined;
    setDataForCreate: React.Dispatch<React.SetStateAction<WasteHandoverPlanModel[]>>;
    disabledField: () => boolean;
}

const HandoverInfoCreate: React.FC<HandoverInfoCreateProps> = ({
    editTableForm,
    editableFormRef,
    dataSource,
    wasteOptions,
    storageOptions,
    dataForCreate,
    dataFromList,
    setDataForCreate,
    disabledField,
}) => {
    const { t } = useTranslation();

    const columns: ProColumns<WasteHandoverPlanModel>[] = [
        {
            title: t('waste-handover-plan.stt'),
            dataIndex: 'stt',
            key: 'stt',
            align: 'center',
            width: 60,
            className: 'min-w-[60px]',
            fixed: 'left',
            editable: false,
            render: (_, record) => {
                return <div style={{ textAlign: 'center', padding: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(record as any)?.stt || '-'}</div>;
            },
        },
        {
            title: t('waste-handover-plan-register.waste-subgroup-contract'),
            key: "wasteGroupingContractName",
            dataIndex: "wasteGroupingContractName",
            width: 180,
            className: 'min-w-[180px]',
            readonly: true,
            fixed: 'left',
            render: (_, record) => {
                return (record as any)?.wasteGroupingContractName || '-';
            },
        },
        {
            title: t('waste-handover-plan-register.waste-scrap'),
            key: "waste",
            dataIndex: "productCode",
            width: 200,
            className: 'min-w-[200px]',
            readonly: true,
            fixed: 'left',
            renderFormItem: (_, config) => {
                const { record } = config;
                // Convert productCode sang string để so sánh
                const productCode = record?.productCode ? String(record.productCode) : '';
                let materialName = '';

                // Ưu tiên hiển thị materialName nếu có
                if (record?.materialName) {
                    materialName = record.materialName;
                }
                // Nếu không có materialName, thử lookup từ wasteOptions
                else if (productCode && wasteOptions.length > 0) {
                    const wasteOption = wasteOptions.find(
                        (opt) => String(opt.value) === productCode
                    );
                    if (wasteOption) {
                        materialName = wasteOption.productName || wasteOption.label;
                    }
                }
                // Fallback về productName (có thể là string hoặc object)
                if (!materialName && record?.productName) {
                    materialName = typeof record.productName === 'string'
                        ? record.productName
                        : (record.productName as any)?.value || '';
                }

                // Hiển thị "Mã | Tên" hoặc chỉ "Mã" nếu không có tên
                if (productCode && materialName) {
                    return <div>{`${productCode} | ${materialName}`}</div>;
                } else if (productCode) {
                    return <div>{productCode}</div>;
                } else {
                    return <div>-</div>;
                }
            },
        },
        {
            title: t('waste-handover-plan.storage-location'),
            key: "storageLocationName",
            dataIndex: "storageLocationName",
            width: 150,
            className: 'min-w-[150px]',
            readonly: true,
            render: (_, record) => {
                return (record as any)?.storageLocationName || '-';
            },
        },
        {
            title: t('waste-handover-plan-register.unit'),
            key: "unit",
            dataIndex: "unit",
            width: 80,
            className: 'min-w-[80px]',
            readonly: true,
            renderFormItem(_, config) {
                const { record } = config;
                return <div>{(record as any)?.unit || t('waste-handover-plan-register.unit-default')}</div>
            },
            render: (_, record) => {
                return (record as any)?.unit || t('waste-handover-plan-register.unit-default');
            },
        },
    ];

    return (
        <CardBox title={t('waste-handover-plan-register.goods-info')} className="col-span-2">
            <EditableProTable<WasteHandoverPlanModel>
                editableFormRef={editableFormRef}
                columns={columns}
                dataSource={dataSource}
                value={dataSource}
                onChange={(newDataSource) => {
                    setDataForCreate(newDataSource as WasteHandoverPlanModel[]);
                }}
                pagination={false}
                className={`${uiStore.theme.name}` + customTableClassName}
                editable={{
                    form: editTableForm,
                    editableKeys: dataSource.length > 0 ? dataSource.map(i => i.wasteHandoverPlanId || '') : undefined,
                }}
                scroll={{ x: 800 }}
                rowKey={'wasteHandoverPlanId'}
                recordCreatorProps={false}
                bordered
            />
        </CardBox>
    );
};

export default HandoverInfoCreate;
